import type {
  AppPlaybackActions,
  GetAppState,
  SetAppState,
} from "../app-store-contract";
import {
  getPlaybackElapsed,
  pausePlayback,
  resetPlayback,
  togglePlayback,
} from "@src/features/playback/playback";
import { calculateAnimationDuration } from "@src/features/playback/animation";
import { canRenderTransform } from "@src/features/visualization/render-safety";
import { identity } from "@src/math/matrix";
import { selectActiveDimensionState } from "../app-state";

export function createPlaybackActions(
  set: SetAppState,
  get: GetAppState,
): AppPlaybackActions {
  return {
    setAnimationMode: (mode) => {
      set((state) => ({
        animation: resetPlayback({ ...state.animation, mode }),
      }));
    },
    togglePlayback: () => {
      set((state) => ({
        animation: togglePlayback(state.animation, performance.now()),
      }));
    },
    pausePlayback: () => {
      set((state) => ({
        animation: pausePlayback(state.animation, performance.now()),
      }));
    },
    completePlayback: () => {
      const state = get();
      if (state.animation.status !== "playing") return false;
      const active = selectActiveDimensionState(state);
      const equation = active.equation;
      const currentTime = performance.now();
      const transform = equation.evaluation.value.totalTransform;
      if (
        getPlaybackElapsed(state.animation, currentTime) <
          calculateAnimationDuration(
            equation.evaluation.value,
            state.animation.mode,
          ) ||
        !canRenderTransform(transform)
      )
        return false;
      const appliedTransforms = { ...state.appliedTransforms };
      if (active.dimension === 2)
        appliedTransforms[2] = [
          ...active.equation.evaluation.value.totalTransform,
        ];
      else
        appliedTransforms[3] = [
          ...active.equation.evaluation.value.totalTransform,
        ];
      set({
        appliedTransforms,
        animation: resetPlayback(state.animation),
      });
      return true;
    },
    resetPlayback: () => {
      set((state) => ({ animation: resetPlayback(state.animation) }));
    },
    resetTransform: () => {
      set((state) => {
        const active = selectActiveDimensionState(state);
        return {
          appliedTransforms:
            active.dimension === 2
              ? {
                  ...state.appliedTransforms,
                  2: identity(2),
                }
              : {
                  ...state.appliedTransforms,
                  3: identity(3),
                },
          animation: resetPlayback(state.animation),
        };
      });
    },
  };
}
